import { addDays, compareIso, daysBetween, isoFromMillis, } from "./dates.js";
import { PING_LEAD_DAYS, PRESUMED_GRACE_DAYS, } from "./item.js";
/**
 * How far `still_here` pushes an expiry out when the event does not say.
 *
 * The item outlived our guess, so the guess was low — but we do not know by how
 * much, and asking would defeat the point of a one-tap answer.
 */
export const DEFAULT_BUMP_DAYS = 3;
export function deriveStatus({ expiryDate, events, today, timezone, }) {
    // Chronological, because a later answer corrects an earlier one. A user who
    // is pinged, ignores it, gets it presumed wasted, then finds it at the back
    // of the fridge and taps "ate it" must end up at `consumed`.
    const ordered = [...events].sort((a, b) => a.at - b.at);
    let resolution = null;
    let resolvedAt = null;
    let resolvedOn = null;
    let effectiveExpiry = expiryDate;
    /**
     * The day the item was put into the app.
     *
     * Adding something is EVIDENCE IT EXISTS. People add things that are already
     * past their date — an initial stocktake, or a jar found at the back of the
     * cupboard — and presuming those wasted the moment they are typed in is both
     * wrong and insulting: the user is looking at the thing.
     */
    let addedOn = null;
    for (const e of ordered) {
        switch (e.type) {
            case "ate":
                resolution = "consumed";
                resolvedAt = e.at;
                resolvedOn = isoFromMillis(e.at, timezone);
                break;
            case "tossed":
                resolution = "wasted";
                resolvedAt = e.at;
                resolvedOn = isoFromMillis(e.at, timezone);
                break;
            case "presumed":
                resolution = "presumed_wasted";
                resolvedAt = e.at;
                resolvedOn = isoFromMillis(e.at, timezone);
                break;
            case "still_here": {
                // Un-resolves: the item is demonstrably still in the house.
                resolution = null;
                resolvedAt = null;
                resolvedOn = null;
                // The bump runs from the day the user ANSWERED, not from the old
                // expiry. "Still here" is a statement about now — measuring from a date
                // that already passed would re-presume the item wasted on the spot,
                // which is what the user just told us is wrong.
                const answeredOn = isoFromMillis(e.at, timezone);
                const from = effectiveExpiry && compareIso(effectiveExpiry, answeredOn) > 0
                    ? effectiveExpiry
                    : answeredOn;
                effectiveExpiry = addDays(from, e.bumpDays ?? DEFAULT_BUMP_DAYS);
                break;
            }
            case "added":
                addedOn = isoFromMillis(e.at, timezone);
                break;
            default:
                // `edited` says nothing about existence.
                break;
        }
    }
    if (resolution) {
        return {
            status: resolution,
            effectiveExpiry,
            daysLeft: null,
            resolvedAt,
            resolvedOn,
        };
    }
    if (!effectiveExpiry) {
        // Correct and indefinite for a restock-only item like salt.
        return {
            status: "undated",
            effectiveExpiry: null,
            daysLeft: null,
            resolvedAt: null,
            resolvedOn: null,
        };
    }
    const daysLeft = daysBetween(today, effectiveExpiry);
    /**
     * The presumption clock starts at the LATER of the expiry and the day it was
     * added — the same shape as the `still_here` bump. An item added after its
     * date gets the full grace period from when we first heard of it, so it is
     * ASKED about rather than written off unseen.
     */
    const presumeFrom = addedOn && compareIso(addedOn, effectiveExpiry) > 0 ? addedOn : effectiveExpiry;
    const daysSincePresumeFrom = daysBetween(presumeFrom, today);
    // Past the grace period with no answer. Derived rather than waiting for the
    // nightly sweep, so the UI is honest even if the sweep has not run — the
    // sweep then writes the matching `presumed` event to make it durable.
    if (daysSincePresumeFrom > PRESUMED_GRACE_DAYS) {
        return {
            status: "presumed_wasted",
            effectiveExpiry,
            daysLeft,
            resolvedAt: null,
            resolvedOn: null,
        };
    }
    // Inside the check-in window and still unanswered.
    if (daysLeft <= PING_LEAD_DAYS) {
        return {
            status: "awaiting",
            effectiveExpiry,
            daysLeft,
            resolvedAt: null,
            resolvedOn: null,
        };
    }
    return {
        status: "active",
        effectiveExpiry,
        daysLeft,
        resolvedAt: null,
        resolvedOn: null,
    };
}
/**
 * The streak: days ending today on which something was resolved and nothing
 * was wasted.
 *
 * Counted over RESOLVED items only. Counting unresolved ones would let a user
 * build a streak purely by ignoring the app, which rewards exactly the
 * behaviour the product exists to fix (docs/product/calendar.md).
 *
 * A day with no resolutions is NEUTRAL — it neither counts nor breaks. Being
 * away for a week should not cost you a streak you did nothing to lose.
 */
export function currentStreak(wasteDays, activityDays, today) {
    const earliest = [...activityDays, ...wasteDays].sort()[0];
    if (!earliest)
        return 0;
    let streak = 0;
    for (let day = today; compareIso(day, earliest) >= 0; day = addDays(day, -1)) {
        if (wasteDays.has(day))
            break;
        if (activityDays.has(day))
            streak++;
    }
    return streak;
}
//# sourceMappingURL=status.js.map